import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * `<WorkspaceProvider>` — resolves the `:slug` route param to a workspace
 * context, switches the server-side session if needed, and builds the CASL
 * ability for the current user inside the current workspace.
 *
 * Per FD `frontend-components.md § Providers` and ND9 (ability is fresh
 * per render, not cached).
 *
 * IMPORTANT: this provider expects to be rendered _below_ `<AuthProvider>`.
 * It also expects an outer router that supplied the slug — but to keep the
 * library framework-free we accept the slug as a prop rather than calling
 * `useParams` ourselves. Consumers (e.g. `apps/dashboard`) pass it in.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, } from "react";
import { defineAbilityFor } from "../shared/ability.js";
import { authClient } from "./auth-client.js";
import { ME_QUERY_KEY, useAuthContext } from "./auth-provider.js";
import { AbilityProvider } from "./can.js";
const WorkspaceContext = createContext(null);
export function WorkspaceProvider({ slug, fallback = null, onNotFound, children, }) {
    const auth = useAuthContext();
    const queryClient = useQueryClient();
    const switchMutation = useMutation({
        mutationFn: (target) => authClient.switchWorkspace({ slug: target }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
    });
    // Stable handles to break referential cycles in the effect / callback deps
    // below — TanStack Query v5 guarantees `mutate` / `mutateAsync` are
    // referentially stable across renders, but TS / lint can't tell.
    const { mutate, mutateAsync, isPending: switchPending } = switchMutation;
    // When the route slug differs from the workspace in the current session,
    // ask the API to rotate the session to the new workspace. Once `/api/me`
    // refetches, this effect short-circuits.
    const currentSlug = auth.workspace?.slug;
    useEffect(() => {
        if (auth.status !== "authenticated")
            return;
        if (!currentSlug)
            return;
        if (currentSlug === slug)
            return;
        if (switchPending)
            return;
        mutate(slug, {
            onError: () => {
                onNotFound?.();
            },
        });
    }, [auth.status, currentSlug, slug, switchPending, mutate, onNotFound]);
    const switchTo = useCallback(async (target) => {
        await mutateAsync(target);
    }, [mutateAsync]);
    const value = useMemo(() => {
        if (auth.status !== "authenticated" || !auth.workspace || auth.workspace.slug !== slug) {
            return null;
        }
        const ability = defineAbilityFor({
            kind: "user",
            userId: auth.user.id,
            // The org tier isn't surfaced to the client yet; ability building
            // only reads `permissions`, so `null` is harmless here.
            orgId: null,
            workspaceId: auth.workspace.id,
            // The real namespaced tokens off `/api/me` (ability building reads
            // only `permissions`; these are carried for shape parity + debugging).
            roles: auth.roles,
            permissions: auth.permissions,
        }, { workspaceId: auth.workspace.id });
        return { workspace: auth.workspace, ability, switchTo };
    }, [auth, slug, switchTo]);
    if (!value) {
        return _jsx(_Fragment, { children: fallback });
    }
    return (_jsx(WorkspaceContext.Provider, { value: value, children: _jsx(AbilityProvider, { ability: value.ability, children: children }) }));
}
/**
 * Internal accessor. Throws if not wrapped in `<WorkspaceProvider>`.
 * Exported via the `useWorkspace` hook in `./hooks/use-workspace.ts`.
 */
export function useWorkspaceContext() {
    const ctx = useContext(WorkspaceContext);
    if (!ctx) {
        throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
    }
    return ctx;
}
//# sourceMappingURL=workspace-provider.js.map